import ElakayImage from '../../../components/images/ElakayImage';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MdPrint } from "react-icons/md";
import { isAuthenticated } from "../../auth";
import { toImageUrl } from "../../../config";
import { listOwnProviderProducts } from "../services/providerProductService";
import PageShell from "../center/PageShell";
import { dropshippingText } from "../i18n";
import { elakayAutoText } from "../../pageInside/dataElakay";
import ProviderProductsFilters, { EMPTY_OWNER_FILTERS, OWNER_SORT } from "../filters/ProviderProductsFilters";
import { setDrawerFilters, clearDrawerFilters, openFilterDrawer } from "../../../components/SIdeDrawer/drawerFilterSlot";
import ProviderPrintModal from "../components/ProviderPrintModal";
import GlobalRatingBadge from "../components/GlobalRatingBadge";
import "./ProviderProducts.css";

const PRINT_LIMIT = 100;
const stripHtml = (html) => typeof html === "string" ? html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

const PAGE_SIZE = 24;
const STATUS_KEY = { active: "t145", draft: "t048", inactive: "t146", archived: "t147" };

// Photo principale = images[0] (l'ordre géré par Edit : la première est la
// principale). Même helper que les cartes Provider côté Seller (toImageUrl),
// chargement paresseux, et repli sur un cadre neutre si absente ou cassée.
// La liste renvoie déjà `images` dans la même requête : aucun appel par produit.
const ProductThumb = ({ product }) => {
  const [broken, setBroken] = useState(false);
  const src = product.images?.[0];
  return (
    <div className="provider-products__thumb">
      {src && !broken ? (
        <ElakayImage preset="product-card" src={toImageUrl(src)} alt={product.name || ""} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <div className="provider-products__no-image" aria-hidden="true"><i className="fas fa-box-open" /></div>
      )}
    </div>
  );
};

// spec §1/§2 (menu Dropshipping In) -- rendue à travers le vrai Layout
// Elakay + le sous-menu partagé, comme toutes les pages du module.
const ProviderProducts = ({ infoSource }) => {
  const t = dropshippingText(infoSource, "providerProductWizard");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // "Filter Products": `applied` is what the list uses; `draft` is what the drawer edits until Submit.
  // Provider Center "Wholesale Management" / "Dropshipping Management" (and the matching Sell Center
  // cards) open this list pre-filtered with ?mode=wholesale|dropshipping -- the same owner filters
  // the drawer exposes, so the user can clear or refine them normally.
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const modeFilters = mode === "wholesale" || mode === "dropshipping" ? { ...EMPTY_OWNER_FILTERS, [mode]: true } : EMPTY_OWNER_FILTERS;
  const [applied, setApplied] = useState(modeFilters);
  const [draft, setDraft] = useState(modeFilters);
  const modeKey = JSON.stringify(modeFilters);
  const firstModeRun = useRef(true);
  useEffect(() => {
    if (firstModeRun.current) { firstModeRun.current = false; return; }
    const next = JSON.parse(modeKey);
    setPage(1); setApplied(next); setDraft(next);
  }, [modeKey]);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  // Print (ticket part 8): a SEPARATE fetch from the screen's own pagination -- the print always
  // covers the full filtered list (up to PRINT_LIMIT), never just the current screen page.
  const [printOpen, setPrintOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState("");
  const [printItems, setPrintItems] = useState([]);
  const openPrint = async () => {
    setPrintOpen(true);
    setPrintLoading(true);
    setPrintError("");
    const token = isAuthenticated()?.token;
    const result = await listOwnProviderProducts(token, {
      page: 1,
      limit: PRINT_LIMIT,
      filters: {
        q, unpublished: applied.unpublished, dropshipping: applied.dropshipping, wholesale: applied.wholesale,
        stock: applied.outOfStock ? "out" : "", sort: "sold", order: applied.asc,
        categoryId: (applied.categoryIds || []).join(","),
      },
    });
    if (result?.error) setPrintError(result.error); else setPrintItems(result.items || []);
    setPrintLoading(false);
  };

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    setError("");
    const token = isAuthenticated()?.token;
    const result = await listOwnProviderProducts(token, {
      page: nextPage,
      limit: Number(applied.limit) || PAGE_SIZE,
      filters: {
        q, unpublished: applied.unpublished, dropshipping: applied.dropshipping, wholesale: applied.wholesale,
        stock: applied.outOfStock ? "out" : "", sort: OWNER_SORT[applied.sortBy] || "created", order: applied.asc,
        categoryId: (applied.categoryIds || []).join(","),
      },
    });
    if (result?.error) {
      setError(result.error);
    } else {
      setItems(result.items || []);
      setTotal(result.total || 0);
    }
    setLoading(false);
  }, [applied, q]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setQ(search.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const filtered = q !== "" || JSON.stringify(applied) !== JSON.stringify(EMPTY_OWNER_FILTERS);
  const limit = Number(applied.limit) || PAGE_SIZE;

  // The detailed filters are the "Filter Products" drawer context (owner's own products).
  const applyRef = useRef(null);
  applyRef.current = (next) => { setPage(1); setApplied(next); setDraft(next); };
  useEffect(() => {
    setDrawerFilters({ owner: "providerProducts", context: "providerProducts", render: ({ click, gridButon, embedded }) => (
      <ProviderProductsFilters draft={draft} setDraft={setDraft} onApply={(next) => applyRef.current(next)} embedded={embedded} click={click} gridButon={gridButon} infoSource={infoSource} />) });
    return () => clearDrawerFilters("providerProducts");
  }, [draft, infoSource]);

  const pages = Math.max(1, Math.ceil(total / limit));

  const printColumns = [
    { key: "name", label: elakayAutoText("h_pps_product", "Product") },
    { key: "description", label: elakayAutoText("h_pps_description", "Description") },
    { key: "quantity", label: elakayAutoText("h_pps_quantity", "Quantity") },
    { key: "sold", label: elakayAutoText("h_pps_sold", "Sold") },
    { key: "price", label: elakayAutoText("h_pps_provider_price", "Provider Price") },
    { key: "status", label: elakayAutoText("h_or_status", "Status") },
  ];
  const printRows = printItems.map((p) => ({
    key: p._id,
    cells: {
      name: p.name || "",
      description: stripHtml(p.description) || "—",
      quantity: p.stock ?? 0,
      sold: p.soldUnits ?? 0,
      price: `$${Number(p.basePrice || 0).toFixed(2)}`,
      status: t(STATUS_KEY[p.status] || "t048"),
    },
  }));

  return (
    <PageShell
      infoSource={infoSource}
      titleElakay="providerProducts"
      title={t("t134")}
      crumb={t("t026")}
      icon="fa-box-open"
      className="provider-products"
      actions={<>
        <button type="button" className="ctr__btn provider-products__print-button" onClick={openPrint} aria-label={elakayAutoText("h_pps_print", "Print this out!")}>
          <MdPrint aria-hidden="true" />
        </button>
        <Link to="/provider/products/add" className="ctr__btn ctr__btn--primary provider-products__add-link">{t("t135")}</Link>
      </>}
    >
      <div className="provider-products__filters" role="search">
        <div className="provider-products__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input type="search" aria-label={t("t134")} placeholder={elakayAutoText("h_pp_search", "Search my products…")} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button type="button" className="ctr__btn provider-products__filter-button" aria-haspopup="dialog" onClick={openFilterDrawer}>
          <i className="fas fa-sliders-h" aria-hidden="true" />{elakayAutoText("h_spm_filters", "Filters")}
        </button>
        {filtered && <button type="button" className="ctr__btn" onClick={() => { setSearch(""); setQ(""); setPage(1); setApplied(EMPTY_OWNER_FILTERS); setDraft(EMPTY_OWNER_FILTERS); }}>{elakayAutoText("h_pff_clear", "Clear All")}</button>}
      </div>

      {loading && (
        <ul className="provider-products__list" aria-busy="true" aria-label={t("t137")}>
          {[0, 1, 2].map((n) => (
            <li key={n} className="provider-products__row provider-products__row--skeleton" aria-hidden="true" />
          ))}
        </ul>
      )}

      {!loading && error && (
        <div className="provider-products__error" role="alert">
          <p className="mb-1">{t("t138")}</p>
          <small className="d-block mb-2 text-muted">{error}</small>
          <button type="button" className="ctr__btn ctr__btn--danger" onClick={() => load(page)}>
            {t("t044")}
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && <div className="ctr__empty provider-products__empty"><i className="fas fa-box-open" aria-hidden="true" /><p>{filtered ? elakayAutoText("h_pp_nomatch", "No products match these filters.") : t("t136")}</p></div>}

      {!loading && !error && items.length > 0 && (
        <ul className="provider-products__list">
          {items.map((product) => {
            const outOfStock = Number(product.stock) <= 0;
            return (
              <li key={product._id} className="provider-products__row">
                <ProductThumb product={product} />
                <div className="provider-products__main">
                  <span className="provider-products__name">{product.name}</span>
                  <GlobalRatingBadge ratings={product.ratings} size="12px" />
                  <span className={`provider-products__status provider-products__status--${product.status}`}>
                    {t(STATUS_KEY[product.status] || "t048")}
                  </span>
                </div>
                <span className="provider-products__price">${Number(product.basePrice || 0).toFixed(2)}</span>
                <span className={outOfStock ? "provider-products__stock provider-products__stock--out" : "provider-products__stock"}>
                  {outOfStock ? t("t140") : t("t141", { n: product.stock })}
                </span>
                <Link to={`/provider/products/${product._id}/edit`} className="provider-products__edit-link">
                  <i className="fas fa-pen" aria-hidden="true" />{t("t139")}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !error && pages > 1 && (
        <div className="provider-products__pager">
          <button type="button" className="ctr__btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t("t142")}
          </button>
          <span>{t("t144", { page, pages })}</span>
          <button type="button" className="ctr__btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            {t("t143")}
          </button>
        </div>
      )}

      <ProviderPrintModal
        show={printOpen} onHide={() => setPrintOpen(false)} infoSource={infoSource}
        title={t("t134")} loading={printLoading} error={printError}
        columns={printColumns} rows={printRows}
        documentTitle="Elakay-Provider-Products" itemNoun={elakayAutoText("h_pps_products_noun", "products")}
      />
    </PageShell>
  );
};

export default ProviderProducts;
